/**
 * Shared API contracts. Owner: foundation. Every module imports envelope + error types from here.
 * Success body: { data: T, meta?: {...} }.  Error body: { error: { code, message, correlationId, details? } }.
 */
import { z } from 'zod';

export const IdSchema = z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/);
export const TenantIdSchema = IdSchema.describe('tenant id');
export const IsoDateTimeSchema = z.string().datetime({ offset: true });

export const ErrorCodeSchema = z.enum([
  'bad_request', 'validation_failed', 'unauthorized', 'forbidden', 'not_found', 'conflict',
  'idempotency_conflict', 'rate_limited', 'feature_disabled', 'payment_required', 'internal_error', 'not_implemented',
  /** staff action on a workspace/agency without an approved access grant (identity.staff_access_approval) */
  'access_approval_required',
]);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

export const ErrorEnvelopeSchema = z.object({
  error: z.object({
    code: ErrorCodeSchema,
    message: z.string(),
    correlationId: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelopeSchema>;

export const PageQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type PageQuery = z.infer<typeof PageQuerySchema>;

export function dataEnvelope<T extends z.ZodTypeAny>(item: T) {
  return z.object({ data: item, meta: z.object({ nextCursor: z.string().nullable().optional() }).optional() });
}

const STATUS: Record<ErrorCode, number> = {
  bad_request: 400, validation_failed: 400, unauthorized: 401, forbidden: 403, not_found: 404, conflict: 409,
  idempotency_conflict: 409, rate_limited: 429, feature_disabled: 404, payment_required: 402, internal_error: 500, not_implemented: 501,
  access_approval_required: 403,
};

/** Throw this from any module; the api error middleware renders the envelope. */
export class ApiError extends Error {
  readonly status: number;
  constructor(readonly code: ErrorCode, message: string, readonly details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = STATUS[code];
  }
}

export const badRequest = (m = 'Bad request', d?: unknown) => new ApiError('bad_request', m, d);
export const unauthorized = (m = 'Authentication required') => new ApiError('unauthorized', m);
export const forbidden = (m = 'Forbidden') => new ApiError('forbidden', m);
/** Use notFound (not forbidden) for cross-tenant access so resource existence is not leaked (IDOR rule). */
export const notFound = (m = 'Not found') => new ApiError('not_found', m);
export const conflict = (m = 'Conflict', d?: unknown) => new ApiError('conflict', m, d);
